import { Link } from "react-router-dom";
import { FEATURE_KEYS, orderColumnKey, type OrderListItem } from "@school-sales-ai/shared";
import { useHasClientFeature } from "@/hooks/useClientFeatures";
import { OrderStatusBadge } from "@/components/orders/order-status";
import type { OrderListResponse } from "@/hooks/useOrders";
import { formatMxn } from "@/lib/format-mxn";
import type { OrderBoardColumn } from "@/lib/order-board";
import { useOrdersStore } from "@/stores/orders.store";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** «Por entregar · Preparando» when the order sits in a custom column. */
function SubColumn({
  order,
  columns,
}: {
  order: OrderListItem;
  columns: OrderBoardColumn[];
}): React.JSX.Element | null {
  if (!order.boardStageId) return null;
  const column = columns.find((c) => c.key === orderColumnKey(order));
  if (!column) return null;
  return (
    <span className="ml-1.5 inline-flex items-center gap-1 text-[11.5px] text-ink-600">
      <span className={`h-2 w-2 rounded-full ${column.swatchClass}`} aria-hidden />
      {column.label}
    </span>
  );
}

type Props = {
  data: OrderListResponse | undefined;
  isLoading: boolean;
  columns: OrderBoardColumn[];
};

/** The table half of /pedidos — every order matching the filters, paginated. */
export function OrderTableView({ data, isLoading, columns }: Props): React.JSX.Element {
  const page = useOrdersStore((s) => s.page);
  const setPage = useOrdersStore((s) => s.setPage);
  const items = data?.items ?? [];
  // «Entrega» is a column only for a shop that delivers; a pickup-only shop
  // would read a column of dashes.
  const delivers = useHasClientFeature(FEATURE_KEYS.delivery);

  if (isLoading) {
    return (
      <p className="mx-4 rounded-lg border border-dashed border-ink-200 p-8 text-center text-sm text-ink-500 md:mx-0">
        Cargando pedidos…
      </p>
    );
  }
  if (items.length === 0) {
    return (
      <p className="mx-4 rounded-lg border border-dashed border-ink-200 p-8 text-center text-sm text-ink-500 md:mx-0">
        No hay pedidos con estos filtros.
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 md:px-0">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[58rem] text-sm">
          <thead>
            <tr className="border-b border-ink-200 text-left text-xs uppercase text-ink-500">
              <th className="py-2 pr-4">Prospecto</th>
              <th className="py-2 pr-4">Estado</th>
              {delivers && <th className="py-2 pr-4">Entrega</th>}
              <th className="py-2 pr-4">Artículos</th>
              <th className="py-2 pr-4 text-right">Total</th>
              <th className="py-2 pr-4">Tomado por</th>
              <th className="py-2 pr-4">Fecha</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} className="border-b border-ink-100">
                <td className="py-3 pr-4">
                  <Link to={`/pedidos/${o.id}`} className="font-medium text-ink-900 underline">
                    {o.leadName ?? "Sin nombre"}
                  </Link>
                  <p className="font-mono text-[11px] text-ink-500">{o.sessionRef}</p>
                </td>
                <td className="whitespace-nowrap py-3 pr-4">
                  <OrderStatusBadge status={o.status} />
                  <SubColumn order={o} columns={columns} />
                </td>
                {delivers && (
                <td className="max-w-[16rem] py-3 pr-4 text-ink-600">
                  {/* The question the person driving has to answer. Truncated
                      because an address with references is long, with the full
                      text on hover — never dropped, since a half address is
                      worse than an obviously-truncated one. */}
                  {o.deliveryAddress ? (
                    <>
                      <span className="block truncate" title={o.deliveryAddress}>
                        {o.deliveryAddress}
                      </span>
                      {o.deliveryZoneName && (
                        <span className="block truncate text-[11px] text-ink-400">
                          Zona {o.deliveryZoneName}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </td>
                )}
                <td className="py-3 pr-4 text-ink-600">{o.itemCount}</td>
                <td className="py-3 pr-4 text-right font-medium text-ink-900">
                  {formatMxn(o.totalCents, o.currency)}
                </td>
                <td className="py-3 pr-4 text-ink-600">
                  {/* The question this whole feature answers. */}
                  {o.confirmedAt ? (o.confirmedByAgent ? "El agente" : "El equipo") : "—"}
                </td>
                <td className="whitespace-nowrap py-3 pr-4 text-ink-600">
                  {formatDate(o.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > data.pageSize && (
        <div className="mt-4 flex items-center justify-end gap-3 text-sm">
          <span className="text-ink-500">
            {(page - 1) * data.pageSize + 1}–{Math.min(page * data.pageSize, data.total)} de{" "}
            {data.total}
          </span>
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700 disabled:opacity-40"
          >
            Anterior
          </button>
          <button
            type="button"
            disabled={page * data.pageSize >= data.total}
            onClick={() => setPage(page + 1)}
            className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700 disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
